import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { RegulifyService } from "../api/RegulifyService";
import DeviceTable from "../components/deepLiterature/DeviceTable";
import DeepAnalysisViewer from "../components/deepLiterature/DeepAnalysisViewer";
import LiteratureComparator from "../components/deepLiterature/LiteratureComparator";
import LiteratureChat from "../components/deepLiterature/LiteratureChat";
import LiteratureFileTable from "../components/deepLiterature/LiteratureFileTable";
import ReportGenerationLoader from "../components/deepLiterature/ReportGenerationLoader";
import { normalizeAnalysisPayload } from "../components/deepLiterature/analysisUtils";
import { useWebSocket } from "../hooks/useWebSocket";
import { useDeepLiteratureStore } from "../store/useDeepLiteratureStore";
import ContentTitle from "../layout/ContentTitle";

const WS_ROUTE = "generate_deep_report";
const WS_INGEST_ROUTE = "run_ingestion_for_full_text";

/**
 * Normalize API response from get_all_study:
 * - results[] items can be: string (study name) or { "<study_name>": { settings, metrics } }
 * - settings.is_drug → Type "Drug" | "Device"
 * - metrics.Unique_PMID_Count → total_journals
 * - metrics.Per_Term_Metrics → search_terms: { term, claims_relevant_articles }[]
 */
function normalizeDevicesFromApi(list) {
  if (!Array.isArray(list)) return [];
  const byName = new Map();
  for (const item of list) {
    if (typeof item === "string") {
      if (!byName.has(item)) {
        byName.set(item, { name: item, type: "Device", total_journals: "—", search_terms: [] });
      }
      continue;
    }
    if (item === null || typeof item !== "object" || Array.isArray(item)) continue;
    const keys = Object.keys(item);
    const studyName = keys[0];
    const payload = item[studyName];
    if (!payload || typeof payload !== "object") {
      if (!byName.has(studyName)) {
        byName.set(studyName, { name: studyName, type: "Device", total_journals: "—", search_terms: [] });
      }
      continue;
    }
    const settings = payload.settings ?? {};
    const metrics = payload.metrics ?? {};
    const isDrug = settings.is_drug === true;
    const totalJournals = metrics.Unique_PMID_Count ?? "—";
    const perTerm = metrics.Per_Term_Metrics ?? {};
    const search_terms = Object.entries(perTerm).map(([term, data]) => ({
      term,
      claims_relevant_articles:
        typeof data === "object" && data !== null && "claims_relevant_articles" in data
          ? data.claims_relevant_articles
          : "—",
    }));
    byName.set(studyName, {
      name: studyName,
      type: isDrug ? "Drug" : "Device",
      total_journals: totalJournals,
      search_terms,
    });
  }
  return Array.from(byName.values());
}

const SCREENS = {
  DEVICE_LIST: "device-list",
  LITERATURE_FILES: "literature-files",
  REPORT_GENERATING: "report-generating",
  ANALYSIS_VIEWER: "analysis-viewer",
  LITERATURE_CHAT: "literature-chat",
  COMPARATOR: "comparator",
};

const DeepLiteratureAnalysis = () => {
  const { connectWebSocket } = useWebSocket();
  const [screen, setScreen] = useState(SCREENS.DEVICE_LIST);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [ingestingDevice, setIngestingDevice] = useState(null);
  const [ingestionCompletedDevice, setIngestionCompletedDevice] = useState(null);
  const [reportCompleted, setReportCompleted] = useState(false);

  const {
    devices,
    setDevices,
    selectedDevice,
    setSelectedDevice,
    literatureFiles,
    setLiteratureFiles,
    analysisJson,
    setAnalysisJson,
    selectedPaperKey,
    setSelectedPaperKey,
    resetForNewDevice,
  } = useDeepLiteratureStore(
    useShallow((s) => ({
      devices: s.devices,
      setDevices: s.setDevices,
      selectedDevice: s.selectedDevice,
      setSelectedDevice: s.setSelectedDevice,
      literatureFiles: s.literatureFiles,
      setLiteratureFiles: s.setLiteratureFiles,
      analysisJson: s.analysisJson,
      setAnalysisJson: s.setAnalysisJson,
      selectedPaperKey: s.selectedPaperKey,
      setSelectedPaperKey: s.setSelectedPaperKey,
      resetForNewDevice: s.resetForNewDevice,
    })),
  );

  const productName = selectedDevice?.name ?? selectedDevice;
  const hasDownloadedFiles = Array.isArray(literatureFiles)
    ? literatureFiles.some((file) => Boolean(file?.file_name ?? file?.filename ?? file?.name))
    : false;

  const isNoPdfResponse = (parsed) => {
    if (!parsed || typeof parsed !== "object") return false;
    const keys = Object.keys(parsed);
    if (keys.length === 0) return true;

    const message = String(parsed?.message || parsed?.detail || "").toLowerCase();
    if (message.includes("no pdf")) return true;

    const filePaths =
      parsed?.file_paths ??
      parsed?.filePaths ??
      parsed?.data?.file_paths ??
      parsed?.data?.filePaths ??
      parsed?.result?.file_paths ??
      parsed?.result?.filePaths ??
      null;
    if (Array.isArray(filePaths) && filePaths.length === 0) return true;

    const manifest = parsed?.manifest ?? parsed?.data?.manifest ?? null;
    const manifestPaths = manifest?.file_paths ?? manifest?.filePaths ?? null;
    if (Array.isArray(manifestPaths) && manifestPaths.length === 0) return true;

    return false;
  };

  useEffect(() => {
    const fetchDevices = async () => {
      setDevicesLoading(true);
      try {
        const data = await RegulifyService.dla_get_devices();
        const list = data?.results ?? data?.study_list ?? (Array.isArray(data) ? data : []);
        const mapped = normalizeDevicesFromApi(list);
        setDevices(mapped);
      } catch (e) {
        toast.error("Failed to load devices.");
        setDevices([]);
      } finally {
        setDevicesLoading(false);
      }
    };
    fetchDevices();
  }, [setDevices]);

  const handleViewLiterature = useCallback(
    async (device) => {
      const deviceName = device?.name ?? device;
      if (!deviceName) {
        toast.error("Device name missing.");
        return;
      }
      if (ingestingDevice) return;

      setIngestingDevice(deviceName);

      let ingestionCompleted = false;
      const proceedToFiles = () => {
        if (ingestionCompleted) return;
        ingestionCompleted = true;
        setIngestionCompletedDevice(deviceName);
        setTimeout(() => {
          setIngestingDevice(null);
          setIngestionCompletedDevice(null);
          setSelectedDevice(device);
          resetForNewDevice();
          setScreen(SCREENS.LITERATURE_FILES);
          setFilesLoading(true);
          RegulifyService.dla_get_literature_files(deviceName)
            .then((data) => {
              const files = data?.files ?? data?.literature_files ?? [];
              setLiteratureFiles(Array.isArray(files) ? files : []);
            })
            .catch(() => {
              toast.error("Failed to load literature files.");
              setLiteratureFiles([]);
            })
            .finally(() => setFilesLoading(false));
        }, 1000);
      };

      connectWebSocket(
        WS_INGEST_ROUTE,
        { device_name: deviceName },
        (parsed) => {
          if (isNoPdfResponse(parsed)) {
            toast.error("No PDFs found. Can't run deep analysis.");
            setIngestingDevice(null);
            setIngestionCompletedDevice(null);
            return;
          }
          const status = String(parsed?.status || "").toLowerCase();
          const message = String(parsed?.message || "").toLowerCase();
          if (
            parsed?.done === true ||
            parsed?.success === true ||
            status === "done" ||
            status === "completed" ||
            status === "success" ||
            message.includes("completed") ||
            message.includes("success")
          ) {
            proceedToFiles();
            return;
          }
        },
        () => {
          if (!ingestionCompleted) {
            toast.error("Ingestion failed or did not complete.");
            setIngestingDevice(null);
          }
        },
        () => {
          if (!ingestionCompleted) {
            toast.error("Ingestion failed or did not complete.");
            setIngestingDevice(null);
          }
        },
      );
    },
    [
      ingestingDevice,
      connectWebSocket,
      setSelectedDevice,
      resetForNewDevice,
      setLiteratureFiles,
    ],
  );

  const handleGenerateReport = useCallback(() => {
    if (!productName) {
      toast.error("No device selected.");
      return;
    }
    setScreen(SCREENS.REPORT_GENERATING);
    setReportCompleted(false);
    connectWebSocket(
      WS_ROUTE,
      { product_name: productName },
      (parsed) => {
        const normalized = normalizeAnalysisPayload(parsed);
        const status = String(parsed?.status || "").toLowerCase();
        const message = String(parsed?.message || "").toLowerCase();
        const isFinal =
          parsed?.done === true ||
          parsed?.success === true ||
          parsed?.final === true ||
          status === "done" ||
          status === "completed" ||
          status === "success" ||
          message.includes("completed") ||
          message.includes("success");

        if (!isFinal) return;
        if (!normalized || Object.keys(normalized).length === 0) return;

        setAnalysisJson(normalized);
        setSelectedPaperKey(
          Object.keys(normalized).length ? Object.keys(normalized)[0] : null,
        );
        setReportCompleted(true);
        setTimeout(() => {
          setScreen(SCREENS.ANALYSIS_VIEWER);
          setReportCompleted(false);
        }, 1000);
        toast.success("Report generated.");
      },
      () => {},
      () => {
        setScreen(SCREENS.LITERATURE_FILES);
        toast.error("Report generation failed.");
      },
    );
  }, [
    productName,
    connectWebSocket,
    setAnalysisJson,
    setSelectedPaperKey,
  ]);

  const handleBackToDeviceList = useCallback(() => {
    setScreen(SCREENS.DEVICE_LIST);
    setSelectedDevice(null);
  }, [setSelectedDevice]);

  const handleBackToLiteratureFiles = useCallback(() => {
    setScreen(SCREENS.LITERATURE_FILES);
  }, []);

  return (
    <div className="flex h-[calc(100vh-40px)] w-full flex-col">
      <ContentTitle title="Deep Literature Analysis" />

      {screen === SCREENS.DEVICE_LIST && (
        <div className="card bg-base-100 mt-4 flex-1 overflow-hidden p-6 shadow-xl">
          <h1 className="text-2xl font-bold text-base-content">
            Deep Literature Analysis
          </h1>
          <p className="mt-1 text-base-content/70">
            Select a device or drug to perform deep literature analysis.
          </p>
          <div className="mt-6">
            <DeviceTable
              devices={devices}
              onDeepAnalyse={handleViewLiterature}
              loading={devicesLoading}
              ingestingDevice={ingestingDevice}
              ingestionCompletedDevice={ingestionCompletedDevice}
            />
          </div>
        </div>
      )}

      {screen === SCREENS.LITERATURE_FILES && (
        <div className="card bg-base-100 mt-4 flex-1 overflow-auto p-6 shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-base-content">
                {productName}
              </h1>
              <h2 className="mt-1 text-lg font-semibold text-base-content/80">
                Literature Files
              </h2>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleBackToDeviceList}
            >
              ← Back
            </button>
          </div>
          <div className="mt-6">
            {hasDownloadedFiles && (
              <div className="card border border-primary bg-base-200">
                <div className="card-body">
                  <h3 className="card-title text-primary">
                    Deep Analysis Report
                  </h3>
                  <p className="text-sm text-base-content/70">
                    Generate an AI powered deep literature analysis report from all
                    selected papers.
                  </p>
                  <div className="card-actions mt-4">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleGenerateReport}
                    >
                      Generate Deep Analysis Report
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="mt-6">
            <LiteratureFileTable
              files={literatureFiles}
              productName={productName}
              loading={filesLoading}
            />
          </div>
        </div>
      )}

      {screen === SCREENS.REPORT_GENERATING && (
        <div className="card bg-base-100 mt-4 flex-1 overflow-hidden p-6 shadow-xl">
          <ReportGenerationLoader completed={reportCompleted} />
        </div>
      )}

      {screen === SCREENS.ANALYSIS_VIEWER && (
        <div className="card bg-base-100 mt-4 flex-1 flex flex-col overflow-hidden p-4 shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleBackToLiteratureFiles}
            >
              ← Back to files
            </button>
          </div>
          <div className="min-h-0 flex-1">
            <DeepAnalysisViewer
              analysisJson={analysisJson}
              selectedPaperKey={selectedPaperKey}
              onSelectPaper={setSelectedPaperKey}
              productName={productName}
              onAskLiterature={() => setScreen(SCREENS.LITERATURE_CHAT)}
              onOpenComparator={() => setScreen(SCREENS.COMPARATOR)}
            />
          </div>
        </div>
      )}

      {screen === SCREENS.COMPARATOR && (
        <div className="card bg-base-100 mt-4 flex-1 flex flex-col overflow-hidden p-4 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setScreen(SCREENS.ANALYSIS_VIEWER)}
            >
              ← Back to analysis
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <LiteratureComparator
              analysisJson={analysisJson}
              productName={productName}
              productType={selectedDevice?.type}
            />
          </div>
        </div>
      )}

      {screen === SCREENS.LITERATURE_CHAT && (
        <div className="card bg-base-100 mt-4 flex-1 flex flex-col overflow-hidden p-4 shadow-xl">
          <div className="min-h-0 flex-1">
            <LiteratureChat
              productName={productName}
              literatureFiles={literatureFiles}
              onBack={() => setScreen(SCREENS.ANALYSIS_VIEWER)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DeepLiteratureAnalysis;
